import { useTranslation } from 'react-i18next';
import Page from '@/components/Page';
import { useForm } from '@mantine/form';
import validate from '@/helpers/validations';
import { Button, Checkbox, TextInput } from '@mantine/core';
import { useState } from 'react';
import { db } from '@/config/firebase';
import useLanguage from '@/hooks/useLanguage';
import StarsImage from '../../assets/images/stars.svg?react'; // eslint-disable-line import/no-unresolved
import EnvelopeImage from '../../assets/images/envelope.svg?react'; // eslint-disable-line import/no-unresolved
import WhatsappImage from '../../assets/images/whatsapp.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './contact.module.css';

const Contact = () => {
  const { t } = useTranslation();
  const { lng } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    validateInputOnBlur: true,
    initialValues: {
      fullName: '',
      phone: '',
      email: '',
      help: '',
      agree: false,
    },
    validate: {
      fullName: (value) => validate(value, {
        required: t('global.validations.required'),
        ...(lng === 'en'
          ? { characters: t('global.validations.characters') }
          : { hebrew: t('global.validations.hebrew') }),
      }),
      phone: (value) => validate(value, {
        required: t('global.validations.required'),
        mobile: t('global.validations.mobile'),
      }),
      email: (value) => validate(value, {
        required: t('global.validations.required'),
        email: t('global.validations.email'),
      }),
      help: (value) => validate(value, {
        required: t('global.validations.required'),
      }),
    },
  });

  const handleErrors = (errors) => {
    const firstErrorPath = Object.keys(errors)[0];
    form.getInputNode(firstErrorPath)?.focus();
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const {
        fullName,
        phone,
        email,
        help,
      } = values;

      db.collection('Leads').add({
        fullName,
        phoneNumber: phone,
        email,
        mailing: help,
        language: lng,
      });

      setSuccess(true);
      setLoading(false);
      setError(true);
      form.reset();
    } catch (err) {
      setSuccess(false);
      setLoading(false);
      setError(true);
    }
  };

  return (
    <Page isFull>
      <div className={classes.container}>
        <div className={classes.content}>
          <StarsImage />
          <div className={classes.details}>
            <div className={classes.icons}>
              <EnvelopeImage />
              <WhatsappImage />
            </div>
            <div>{t('pages.contact.details.address')}</div>
            <div>
              <a href={`tel:${t('pages.contact.details.phone')}`} className={classes.contactLink}>
                {t('pages.contact.details.phone')}
              </a>
            </div>
            <div>
              <a href={`mailto:${t('pages.contact.details.email')}`} className={classes.contactLink}>
                {t('pages.contact.details.email')}
              </a>
            </div>
          </div>
        </div>
        <form onSubmit={form.onSubmit(handleSubmit, handleErrors)}>
          <h2 className={classes.title}>{t('pages.contact.title')}</h2>
          <TextInput
            label={t('pages.contact.form.name')}
            data-f="fullName"
            maxLength="20"
            key={form.key('fullName')}
            {...form.getInputProps('fullName')}
          />
          <TextInput
            label={t('pages.contact.form.phone')}
            data-f="phone"
            maxLength="10"
            key={form.key('phone')}
            {...form.getInputProps('phone')}
          />
          <TextInput
            label={t('pages.contact.form.email')}
            data-f="email"
            key={form.key('email')}
            {...form.getInputProps('email')}
          />
          <TextInput
            label={t('pages.contact.form.help')}
            data-f="help"
            key={form.key('help')}
            {...form.getInputProps('help')}
          />
          <Checkbox
            className={classes.agree}
            label={t('pages.contact.form.agree')}
            data-f="agree"
            key={form.key('agree')}
            {...form.getInputProps('agree')}
          />
          <Button className={classes.button} variant="filled" type="submit" data-f="continue" loading={loading}>
            {t('pages.contact.form.send')}
          </Button>
          {success && <span className={classes.success}>{t('pages.contact.form.success')}</span>}
          {error && <span className={classes.error}>{t('pages.contact.form.error')}</span>}
        </form>
        <div className={classes.info}>
        </div>
      </div>
    </Page>
  );
};

export default Contact;
